import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import client, { setToken, getToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BRAND } from "../mock";
import { toast } from "sonner";

// Handles the redirect back from Emergent Google auth.
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

// localStorage-backed dedupe (survives even a full page reload) so a single-use
// Google session_id is NEVER exchanged more than once.
const DEDUP_KEY = "mb_oauth_handled";
function markHandled(sid) {
  try {
    const raw = JSON.parse(localStorage.getItem(DEDUP_KEY) || "null");
    if (raw && raw.sid === sid && Date.now() - raw.at < 120000) return false; // already handled
    localStorage.setItem(DEDUP_KEY, JSON.stringify({ sid, at: Date.now() }));
    return true;
  } catch (e) {
    return true;
  }
}

export default function AuthCallback() {
  const { applyGoogleSession } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // session_id can arrive in the URL fragment (#session_id=) or query (?session_id=)
    const hash = window.location.hash || "";
    const search = window.location.search || "";
    const match = (hash + "&" + search).match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    // Navigate WITHOUT a full page reload so React state (and this guard) is preserved.
    const go = (path) => navigate(path, { replace: true });

    if (!sessionId) {
      go(getToken() ? "/dashboard" : "/login");
      return;
    }

    // If this exact session was already handled, don't exchange it again.
    if (!markHandled(sessionId)) {
      go(getToken() ? "/dashboard" : "/login");
      return;
    }

    // Clear the session_id from the address bar.
    try { window.history.replaceState(null, "", window.location.pathname); } catch (e) { console.warn("Could not clean the URL", e); }

    let settled = false;
    const finish = (path) => { if (settled) return; settled = true; go(path); };

    // Safety net: never leave the user stuck on the spinner.
    const timer = setTimeout(() => {
      if (getToken()) finish("/dashboard");
      else { toast.error("Google sign-in timed out", { description: "Please try again, or sign in with email." }); finish("/login"); }
    }, 15000);

    (async () => {
      try {
        const res = await client.post("/auth/google/session", null, {
          headers: { "X-Session-ID": sessionId },
        });
        setToken(res.data.token);
        applyGoogleSession(res.data.token, res.data.user);
        clearTimeout(timer);
        toast.success("Signed in with Google");
        finish("/dashboard");
      } catch (e) {
        clearTimeout(timer);
        if (getToken()) { finish("/dashboard"); return; }
        toast.error("Google sign-in didn't complete", {
          description: "That sign-in link expired. Tap Continue with Google again, or use email + password.",
        });
        finish("/login");
      }
    })();

    return () => clearTimeout(timer);
  }, [applyGoogleSession, navigate]);

  return (
    <div className="min-h-screen grid place-items-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <img src={BRAND.logo} alt="Moroccan Brothers" className="h-28 w-auto max-w-[340px] object-contain" />
        <div className="h-9 w-9 rounded-full border-2 border-brand-red border-t-transparent animate-spin" />
        <p className="text-sm text-muted-foreground">Signing you in…</p>
        <button onClick={() => navigate("/login", { replace: true })}
          className="mt-2 text-xs text-muted-foreground underline hover:text-white transition-colors">
          Taking too long? Back to sign in
        </button>
      </div>
    </div>
  );
}
